import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { uploadFilePublic } from "../lib/api";
import { 
  Eye, EyeOff, Truck, Lock, Mail, User, Building2, Phone, MapPin, 
  FileText, Upload, Check, AlertCircle, ArrowLeft, ArrowRight, ChefHat, X,
  Plus, Trash2, UtensilsCrossed, Euro
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STEPS = [
  { id: 1, title: "Compte", icon: User },
  { id: 2, title: "Food Truck", icon: Truck },
  { id: 3, title: "Menu", icon: UtensilsCrossed },
  { id: 4, title: "Entreprise", icon: Building2 },
  { id: 5, title: "Documents", icon: FileText },
];

// Catégories de plats
const DISH_CATEGORIES = [
  { id: "entree", label: "Entrée" },
  { id: "plat", label: "Plat" },
  { id: "dessert", label: "Dessert" },
  { id: "boisson", label: "Boisson" },
];

// Allergènes standards
const ALLERGENES = [
  "Gluten", "Crustacés", "Oeufs", "Poisson", "Arachides", "Soja",
  "Lait", "Fruits à coque", "Céleri", "Moutarde", "Sésame",
  "Sulfites", "Lupin", "Mollusques"
];

export default function RegisterFoodTruckPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    // User
    email: "",
    password: "",
    nom_complet: "",
    // Food Truck
    nom_food_truck: "",
    specialites: [],
    telephone: "",
    adresse: "",
    // Enterprise
    siren: "",
    siret: "",
    // Documents without expiration
    document_kbis_id: "",
    document_kbis_name: "",
    document_haccp_id: "",
    document_haccp_name: "",
    // Documents with expiration
    document_identite_id: "",
    document_identite_name: "",
    document_identite_expiration: "",
    document_assurance_id: "",
    document_assurance_name: "",
    document_assurance_expiration: "",
    document_carte_commercant_id: "",
    document_carte_commercant_name: "",
    document_carte_commercant_expiration: "",
    document_assurance_vehicule_id: "",
    document_assurance_vehicule_name: "",
    document_assurance_vehicule_expiration: "",
  });
  
  const [specialitesOptions, setSpecialitesOptions] = useState([]);
  const [showSpecialites, setShowSpecialites] = useState(false);
  const [specialiteSearch, setSpecialiteSearch] = useState("");
  
  // Menu dishes state
  const [dishes, setDishes] = useState([]);
  const [editingDish, setEditingDish] = useState(null);
  const [showDishForm, setShowDishForm] = useState(false);

  // Load specialites on mount
  useEffect(() => {
    const loadSpecialites = async () => {
      try {
        const response = await axios.get(`${API_URL}/specialites`);
        setSpecialitesOptions(response.data.specialites);
      } catch (error) {
        console.error("Error loading specialites:", error);
      }
    };
    loadSpecialites();
  }, []);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSpecialite = (spec) => {
    setFormData(prev => {
      const current = prev.specialites;
      if (current.includes(spec)) {
        return { ...prev, specialites: current.filter(s => s !== spec) };
      } else {
        return { ...prev, specialites: [...current, spec] };
      }
    });
  };

  const filteredSpecialites = specialitesOptions.filter(s => 
    s.toLowerCase().includes(specialiteSearch.toLowerCase())
  );

  const handleFileUpload = async (file, docType) => {
    if (!file) return;
    
    setUploadingDoc(docType);
    try {
      const response = await uploadFilePublic(file);
      const fileId = response.data.url.split('/').pop();
      updateField(`document_${docType}_id`, fileId);
      updateField(`document_${docType}_name`, file.name);
      toast.success(`${file.name} téléversé`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors du téléversement";
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setUploadingDoc(null);
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.email || !formData.password || !formData.nom_complet) {
          toast.error("Veuillez remplir tous les champs");
          return false;
        }
        if (formData.password.length < 6) {
          toast.error("Le mot de passe doit contenir au moins 6 caractères");
          return false;
        }
        return true;
      case 2:
        if (!formData.nom_food_truck || formData.specialites.length === 0 || !formData.telephone) {
          toast.error("Veuillez remplir tous les champs obligatoires et sélectionner au moins une spécialité");
          return false;
        }
        return true;
      case 3:
        // Menu step - optional but encouraged
        if (dishes.length === 0) {
          toast.info("Vous pourrez ajouter votre menu plus tard depuis votre espace");
        }
        return true;
      case 4:
        if (!formData.siren || !formData.siret) {
          toast.error("SIREN et SIRET sont obligatoires");
          return false;
        }
        if (formData.siren.length !== 9) {
          toast.error("Le SIREN doit contenir 9 chiffres");
          return false;
        }
        if (formData.siret.length !== 14) {
          toast.error("Le SIRET doit contenir 14 chiffres");
          return false;
        }
        return true;
      case 5:
        if (!formData.document_kbis_id) {
          toast.error("Le Kbis est obligatoire");
          return false;
        }
        if (!formData.document_haccp_id) {
          toast.error("L'attestation HACCP est obligatoire");
          return false;
        }
        if (!formData.document_assurance_id || !formData.document_assurance_expiration) {
          toast.error("L'assurance RC Pro est obligatoire avec sa date d'expiration");
          return false;
        }
        if (!formData.document_carte_commercant_id || !formData.document_carte_commercant_expiration) {
          toast.error("La carte commerçant ambulant est obligatoire avec sa date d'expiration");
          return false;
        }
        if (!formData.document_assurance_vehicule_id || !formData.document_assurance_vehicule_expiration) {
          toast.error("L'assurance véhicule est obligatoire avec sa date d'expiration");
          return false;
        }
        if (!formData.document_identite_id || !formData.document_identite_expiration) {
          toast.error("La pièce d'identité est obligatoire avec sa date d'expiration");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Dish management functions
  const handleAddDish = () => {
    setEditingDish({
      nom: "",
      description: "",
      prix: "",
      categorie: "plat",
      ingredients: "",
      allergenes: [],
      vegetarien: false,
      vegan: false,
      sans_gluten: false,
    });
    setShowDishForm(true);
  };

  const handleSaveDish = () => {
    if (!editingDish.nom || !editingDish.prix) {
      toast.error("Nom et prix sont obligatoires");
      return;
    }
    
    const dishToSave = {
      ...editingDish,
      prix: parseFloat(editingDish.prix),
      ingredients: editingDish.ingredients 
        ? editingDish.ingredients.split(",").map(s => s.trim()).filter(Boolean)
        : [],
      id: editingDish.id || Date.now().toString(),
    };
    
    if (editingDish.id) {
      setDishes(dishes.map(d => d.id === editingDish.id ? dishToSave : d));
    } else {
      setDishes([...dishes, dishToSave]);
    }
    
    setShowDishForm(false);
    setEditingDish(null);
    toast.success(editingDish.id ? "Plat modifié" : "Plat ajouté");
  };

  const handleEditDish = (dish) => {
    setEditingDish({
      ...dish,
      ingredients: Array.isArray(dish.ingredients) ? dish.ingredients.join(", ") : "",
    });
    setShowDishForm(true);
  };

  const handleDeleteDish = (dishId) => {
    setDishes(dishes.filter(d => d.id !== dishId));
    toast.success("Plat supprimé");
  };

  const toggleAllergene = (allergene) => {
    const current = editingDish.allergenes || [];
    if (current.includes(allergene)) {
      setEditingDish({ ...editingDish, allergenes: current.filter(a => a !== allergene) });
    } else {
      setEditingDish({ ...editingDish, allergenes: [...current, allergene] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(5)) return;

    setIsLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        nom_complet: formData.nom_complet,
        nom_food_truck: formData.nom_food_truck,
        specialites: formData.specialites,
        siren: formData.siren,
        siret: formData.siret,
        telephone: formData.telephone,
        adresse: formData.adresse || "",
        // Menu dishes
        dishes: dishes.map(d => ({
          nom: d.nom,
          description: d.description || "",
          prix: d.prix,
          categorie: d.categorie,
          ingredients: d.ingredients,
          allergenes: d.allergenes,
          vegetarien: d.vegetarien,
          vegan: d.vegan,
          sans_gluten: d.sans_gluten,
          disponible: true,
        })),
        // Documents without expiration
        document_kbis_id: formData.document_kbis_id,
        document_haccp_id: formData.document_haccp_id,
        // Documents with expiration
        document_identite_id: formData.document_identite_id,
        document_identite_expiration: formData.document_identite_expiration,
        document_assurance_id: formData.document_assurance_id,
        document_assurance_expiration: formData.document_assurance_expiration,
        document_carte_commercant_id: formData.document_carte_commercant_id,
        document_carte_commercant_expiration: formData.document_carte_commercant_expiration,
        document_assurance_vehicule_id: formData.document_assurance_vehicule_id,
        document_assurance_vehicule_expiration: formData.document_assurance_vehicule_expiration,
      };

      const response = await axios.post(`${API_URL}/auth/register-food-truck`, payload);
      const { access_token } = response.data;
      
      localStorage.setItem("token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      toast.success("Inscription réussie ! Veuillez maintenant signer le contrat d'adhésion.");
      navigate("/contrat");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setIsLoading(false);
    }
  };

  const renderDocumentUpload = (docType, label, required = true, hasExpiration = false) => (
    <div className="space-y-3 p-4 border border-dashed border-[#E5E0DC] rounded-lg bg-white/50">
      <div className="flex items-center justify-between">
        <Label className="text-secondary font-semibold">{label} {required && <span className="text-red-500">*</span>}</Label>
        {formData[`document_${docType}_id`] && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <Check className="w-4 h-4" /> Téléversé
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-3">
        <label className="flex-1">
          <div className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            formData[`document_${docType}_id`] 
              ? 'border-green-300 bg-green-50' 
              : 'border-[#E5E0DC] hover:border-primary hover:bg-primary/5'
          }`}>
            {uploadingDoc === docType ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            ) : (
              <>
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {formData[`document_${docType}_name`] || "Choisir un fichier"}
                </span>
              </>
            )}
          </div>
          <input
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => handleFileUpload(e.target.files[0], docType)}
            data-testid={`upload-${docType}`}
          />
        </label>
      </div>
      
      {hasExpiration && (
        <div className="space-y-1">
          <Label className="text-sm text-muted-foreground">Date d'expiration <span className="text-red-500">*</span></Label>
          <Input
            type="date"
            value={formData[`document_${docType}_expiration`]}
            onChange={(e) => updateField(`document_${docType}_expiration`, e.target.value)}
            className="border-[#E5E0DC]"
            data-testid={`expiration-${docType}`}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F7F5] via-[#F0EBE6] to-[#F9F7F5] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-2xl relative z-10">
        {/* Header */}
        <div className="text-center mb-6 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-3">
            <ChefHat className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading text-2xl font-black text-primary">Inscription Food Truck</h1>
          <p className="text-muted-foreground mt-1 text-sm">Rejoignez le collectif Food Truck United</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                  currentStep >= step.id 
                    ? 'bg-primary text-white' 
                    : 'bg-white border-2 border-[#E5E0DC] text-muted-foreground'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`w-16 sm:w-24 h-1 mx-1 rounded ${
                    currentStep > step.id ? 'bg-primary' : 'bg-[#E5E0DC]'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground px-1">
            {STEPS.map(step => (
              <span key={step.id} className={currentStep >= step.id ? 'text-primary font-medium' : ''}>
                {step.title}
              </span>
            ))}
          </div>
        </div>

        <Card className="bg-white/80 backdrop-blur border-[#E5E0DC] shadow-xl animate-fade-in">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {STEPS[currentStep - 1].title}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && "Créez votre compte utilisateur"}
              {currentStep === 2 && "Informations sur votre food truck"}
              {currentStep === 3 && "Informations de votre entreprise"}
              {currentStep === 5 && "Documents obligatoires avec dates d'expiration"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit}>
              {/* Step 1: User Account */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nom_complet" className="text-secondary font-semibold">
                      Nom complet <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="nom_complet"
                        type="text"
                        placeholder="Jean Dupont"
                        value={formData.nom_complet}
                        onChange={(e) => updateField("nom_complet", e.target.value)}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-nom"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-secondary font-semibold">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="email@example.com"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-email"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-secondary font-semibold">
                      Mot de passe <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Minimum 6 caractères"
                        value={formData.password}
                        onChange={(e) => updateField("password", e.target.value)}
                        className="pl-10 pr-10 h-12 border-[#E5E0DC]"
                        data-testid="register-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Food Truck Info */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-secondary font-semibold">
                      Nom du Food Truck <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Le Camion Gourmand"
                        value={formData.nom_food_truck}
                        onChange={(e) => updateField("nom_food_truck", e.target.value)}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-foodtruck-name"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-secondary font-semibold">
                      Spécialités <span className="text-red-500">*</span>
                    </Label>
                    
                    {/* Selected specialties */}
                    {formData.specialites.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.specialites.map((spec) => (
                          <span
                            key={spec}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-white rounded-full text-sm"
                          >
                            {spec}
                            <button
                              type="button"
                              onClick={() => toggleSpecialite(spec)}
                              className="hover:bg-white/20 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Search and dropdown */}
                    <div className="relative">
                      <ChefHat className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
                      <Input
                        type="text"
                        placeholder="Rechercher une spécialité..."
                        value={specialiteSearch}
                        onChange={(e) => setSpecialiteSearch(e.target.value)}
                        onFocus={() => setShowSpecialites(true)}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-specialite-search"
                      />
                      
                      {showSpecialites && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-[#E5E0DC] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          <div className="p-2 border-b border-[#E5E0DC] sticky top-0 bg-white">
                            <span className="text-xs text-muted-foreground">
                              {formData.specialites.length} sélectionnée(s) - Cliquez pour ajouter/retirer
                            </span>
                            <button
                              type="button"
                              onClick={() => setShowSpecialites(false)}
                              className="float-right text-muted-foreground hover:text-primary"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          {filteredSpecialites.map((spec) => (
                            <div
                              key={spec}
                              onClick={() => toggleSpecialite(spec)}
                              className={`px-4 py-2 cursor-pointer flex items-center justify-between hover:bg-[#F0EBE6] ${
                                formData.specialites.includes(spec) ? 'bg-primary/10 text-primary font-medium' : ''
                              }`}
                            >
                              <span>{spec}</span>
                              {formData.specialites.includes(spec) && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </div>
                          ))}
                          {filteredSpecialites.length === 0 && (
                            <div className="px-4 py-3 text-muted-foreground text-sm">
                              Aucune spécialité trouvée
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Sélectionnez une ou plusieurs spécialités
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-secondary font-semibold">
                      Téléphone <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="tel"
                        placeholder="06 12 34 56 78"
                        value={formData.telephone}
                        onChange={(e) => updateField("telephone", e.target.value)}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-telephone"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-secondary font-semibold">Adresse</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="123 Rue de la Cuisine, 75001 Paris"
                        value={formData.adresse}
                        onChange={(e) => updateField("adresse", e.target.value)}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-adresse"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Menu */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <UtensilsCrossed className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-700">
                        <p className="font-medium">Configurez votre carte</p>
                        <p>Ajoutez vos plats maintenant ou plus tard depuis votre espace. Les prix pourront être modifiés à tout moment.</p>
                      </div>
                    </div>
                  </div>

                  {/* Dish Form */}
                  {showDishForm ? (
                    <div className="border border-[#E5E0DC] rounded-lg p-4 space-y-4 bg-white">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-secondary">
                          {editingDish?.id ? "Modifier le plat" : "Nouveau plat"}
                        </h3>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setShowDishForm(false); setEditingDish(null); }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Nom du plat <span className="text-red-500">*</span></Label>
                          <Input
                            value={editingDish?.nom || ""}
                            onChange={(e) => setEditingDish({ ...editingDish, nom: e.target.value })}
                            placeholder="Ex: Pad Thaï"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Prix (€) <span className="text-red-500">*</span></Label>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              type="text"
                              inputMode="decimal"
                              value={editingDish?.prix || ""}
                              onChange={(e) => setEditingDish({ ...editingDish, prix: e.target.value })}
                              placeholder="12.50"
                              className="pl-9"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Catégorie</Label>
                        <Select
                          value={editingDish?.categorie || "plat"}
                          onValueChange={(v) => setEditingDish({ ...editingDish, categorie: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DISH_CATEGORIES.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={editingDish?.description || ""}
                          onChange={(e) => setEditingDish({ ...editingDish, description: e.target.value })}
                          placeholder="Nouilles sautées aux crevettes..."
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Ingrédients (séparés par des virgules)</Label>
                        <Input
                          value={editingDish?.ingredients || ""}
                          onChange={(e) => setEditingDish({ ...editingDish, ingredients: e.target.value })}
                          placeholder="Nouilles, crevettes, cacahuètes..."
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Allergènes</Label>
                        <div className="flex flex-wrap gap-2">
                          {ALLERGENES.map((allergene) => (
                            <Badge
                              key={allergene}
                              variant={(editingDish?.allergenes || []).includes(allergene) ? "default" : "outline"}
                              className="cursor-pointer text-xs"
                              onClick={() => toggleAllergene(allergene)}
                            >
                              {allergene}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-4 pt-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editingDish?.vegetarien || false}
                            onCheckedChange={(v) => setEditingDish({ ...editingDish, vegetarien: v })}
                          />
                          <Label className="text-sm">Végétarien</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editingDish?.vegan || false}
                            onCheckedChange={(v) => setEditingDish({ ...editingDish, vegan: v })}
                          />
                          <Label className="text-sm">Vegan</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editingDish?.sans_gluten || false}
                            onCheckedChange={(v) => setEditingDish({ ...editingDish, sans_gluten: v })}
                          />
                          <Label className="text-sm">Sans gluten</Label>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => { setShowDishForm(false); setEditingDish(null); }}
                        >
                          Annuler
                        </Button>
                        <Button type="button" onClick={handleSaveDish}>
                          {editingDish?.id ? "Modifier" : "Ajouter"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Add dish button */}
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-14 border-dashed border-2"
                        onClick={handleAddDish}
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Ajouter un plat
                      </Button>

                      {/* Dishes list */}
                      {dishes.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-secondary font-semibold">
                            Vos plats ({dishes.length})
                          </Label>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {dishes.map((dish) => (
                              <div
                                key={dish.id}
                                className="flex items-center justify-between p-3 bg-white border border-[#E5E0DC] rounded-lg"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{dish.nom}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {DISH_CATEGORIES.find(c => c.id === dish.categorie)?.label}
                                    </Badge>
                                    {dish.vegetarien && <Badge className="text-xs bg-green-100 text-green-700">Végé</Badge>}
                                    {dish.vegan && <Badge className="text-xs bg-green-100 text-green-700">Vegan</Badge>}
                                  </div>
                                  <p className="text-sm text-primary font-semibold">{parseFloat(dish.prix).toFixed(2)}€</p>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditDish(dish)}
                                  >
                                    <ChefHat className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteDish(dish.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {dishes.length === 0 && (
                        <p className="text-center text-muted-foreground text-sm py-4">
                          Aucun plat ajouté. Vous pourrez compléter votre menu plus tard.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Step 4: Enterprise Info */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-700">
                        <p className="font-medium">Information</p>
                        <p>Le SIREN (9 chiffres) et le SIRET (14 chiffres) sont obligatoires pour l'inscription.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-secondary font-semibold">
                      SIREN <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="123456789"
                        value={formData.siren}
                        onChange={(e) => updateField("siren", e.target.value.replace(/\D/g, '').slice(0, 9))}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-siren"
                        maxLength={9}
                        required
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">{formData.siren.length}/9 chiffres</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-secondary font-semibold">
                      SIRET <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="12345678901234"
                        value={formData.siret}
                        onChange={(e) => updateField("siret", e.target.value.replace(/\D/g, '').slice(0, 14))}
                        className="pl-10 h-12 border-[#E5E0DC]"
                        data-testid="register-siret"
                        maxLength={14}
                        required
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">{formData.siret.length}/14 chiffres</p>
                  </div>
                </div>
              )}

              {/* Step 5: Documents */}
              {currentStep === 5 && (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div className="text-sm text-amber-700">
                        <p className="font-medium">Documents obligatoires</p>
                        <p>L'accès sera bloqué si un document avec date d'expiration expire.</p>
                      </div>
                    </div>
                  </div>

                  {/* Documents without expiration */}
                  <p className="text-sm font-semibold text-secondary">Documents administratifs</p>
                  {renderDocumentUpload("kbis", "Extrait Kbis", true, false)}
                  {renderDocumentUpload("haccp", "Attestation HACCP", true, false)}
                  
                  {/* Documents with expiration */}
                  <p className="text-sm font-semibold text-secondary mt-6">Documents avec date d'expiration</p>
                  {renderDocumentUpload("identite", "Pièce d'identité (CNI ou Passeport)", true, true)}
                  {renderDocumentUpload("assurance", "Assurance RC Professionnelle", true, true)}
                  {renderDocumentUpload("carte_commercant", "Carte Commerçant Ambulant", true, true)}
                  {renderDocumentUpload("assurance_vehicule", "Assurance Véhicule", true, true)}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t border-[#E5E0DC]">
                {currentStep > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="border-[#E5E0DC]"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Précédent
                  </Button>
                ) : (
                  <Link to="/login">
                    <Button variant="ghost" className="text-muted-foreground">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour
                    </Button>
                  </Link>
                )}

                {currentStep < 5 ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="next-step-btn"
                  >
                    Suivant
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    className="bg-primary hover:bg-primary-hover"
                    disabled={isLoading}
                    data-testid="submit-registration-btn"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        S'inscrire
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Déjà membre ?{" "}
          <Link to="/login" className="text-primary hover:underline font-medium">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}
